import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Target, BarChart3 } from "lucide-react";
import { Header } from "../components/Header";
import { GameHero } from "../components/GameHero";
import { AuthDialog } from "../components/AuthDialog";
import { PremiumDialog } from "../components/PremiumDialog";
import { GeneratorTab } from "../components/tabs/GeneratorTab";
import { AnalyzerTab } from "../components/tabs/AnalyzerTab";
import { DashboardTab } from "../components/tabs/DashboardTab";
import { GAMES } from "../data/games";
import { Under18Badge } from "../components/FdjAffiliate";
import { api } from "../lib/api";

const TABS = [
  { id: "generator", label: "Générateur", icon: Sparkles },
  { id: "analyzer", label: "Analyseur", icon: Target },
  { id: "dashboard", label: "Statistiques", icon: BarChart3 },
];

export default function AppShell() {
  const [game, setGame] = useState("loto");
  const [tab, setTab] = useState("generator");
  const [draws, setDraws] = useState([]);
  const [drawCount, setDrawCount] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [affiliate, setAffiliate] = useState({});
  const [jackpots, setJackpots] = useState({});
  const [nextDraws, setNextDraws] = useState({});
  const cfg = GAMES[game];

  const loadDraws = useCallback(async (g) => {
    try {
      const { data } = await api.get(`/draws/${g}`);
      setDraws(data.draws || []);
      setDrawCount(typeof data.count === "number" ? data.count : (data.draws || []).length);
    } catch {
      setDraws([]);
      setDrawCount(0);
    }
  }, []);

  const loadAffiliate = useCallback(async () => {
    try {
      const { data } = await api.get("/affiliate-links");
      setAffiliate(data.links || {});
    } catch {
      setAffiliate({});
    }
  }, []);

  useEffect(() => {
    loadDraws(game);
  }, [game, loadDraws]);

  useEffect(() => {
    loadAffiliate();
  }, [loadAffiliate]);

  useEffect(() => {
    // Refresh live jackpots + next draw dates from fdj.fr on each app load
    api.get("/jackpots").then(({ data }) => {
      setJackpots(data.jackpots || {});
      setNextDraws(data.next_draws || {});
    }).catch(() => { setJackpots({}); setNextDraws({}); });
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header
        game={game}
        onSelectGame={(g) => setGame(g)}
        onOpenAuth={() => setAuthOpen(true)}
        onOpenPremium={() => setPremiumOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <GameHero game={game} drawCount={drawCount} affiliateUrl={affiliate[game]} jackpot={jackpots[game]} nextDraw={nextDraws[game]} />

        {/* Tabs */}
        <div className="flex items-center gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200 mb-8 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`nav-tab-${t.id}`}
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${active ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                style={active ? { color: cfg.primary } : {}}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={tab + game} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            {tab === "generator" && (
              <GeneratorTab game={game} draws={draws} affiliateUrl={affiliate[game]} onNeedAuth={() => setAuthOpen(true)} onNeedPremium={() => setPremiumOpen(true)} />
            )}
            {tab === "analyzer" && <AnalyzerTab game={game} draws={draws} />}
            {tab === "dashboard" && <DashboardTab game={game} draws={draws} />}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="bg-[#0B132B] text-white/60 text-center text-xs py-6 mt-10 px-4">
        <div className="flex flex-col items-center gap-3">
          <Under18Badge size={44} />
          <p className="max-w-2xl">
            ProbaLoto · Outil statistique indépendant, non affilié officiellement à la FDJ · Les liens « Jouer sur FDJ » sont des liens d'affiliation. Interdit aux mineurs. Jouer comporte des risques : endettement, isolement, dépendance. Appelez le 09 74 75 13 13 (appel non surtaxé).
          </p>
        </div>
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <PremiumDialog open={premiumOpen} onOpenChange={setPremiumOpen} />
    </div>
  );
}
